import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Search, ShieldCheck, Download } from "lucide-react";
import ExportExcelButton from "@/components/ExportExcelButton";

const STATUS_CLR = { active: "#10B981", expired: "#EF4444", void: "#64748B" };

export default function Warranties() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/warranties?${q ? `q=${encodeURIComponent(q)}&` : ""}${status ? `status=${status}` : ""}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const downloadPdf = (id) => {
    const token = localStorage.getItem("cpl_token");
    fetch(`${API_BASE}/warranties/${id}/certificate.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const url = window.URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `warranty_${id}.pdf`; a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Coverage</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Warranties</h1>
        </div>
        <ExportExcelButton path="/export/warranties" filename="warranties.xlsx"
          testid="export-warranties-btn" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input
            data-testid="warranty-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search warranty #, customer, vehicle, PPF code..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
          />
        </div>
        <select data-testid="warranty-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {loading ? <div className="text-sm text-[#475569]">Loading…</div> :
        items.length === 0 ? (
          <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">
            <ShieldCheck size={28} className="mx-auto mb-3" /> No warranties found.
          </div>
        ) : (
          <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden">
            <table className="w-full text-sm" data-testid="warranties-table">
              <thead><tr className="border-b border-[#E5E7EB] text-left">
                {["Warranty #", "Customer", "Vehicle", "Product", "Issued", "Expires", "Status", ""].map(h =>
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{h}</th>)}
              </tr></thead>
              <tbody>{items.map(w => (
                <tr key={w.id} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3 font-mono text-[#E63946]">{w.warranty_number}</td>
                  <td className="px-4 py-3">
                    <div>{w.customer_name}</div>
                    <div className="text-[11px] text-[#64748B] font-mono">+91 {w.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{w.vehicle_reg_no}</td>
                  <td className="px-4 py-3 text-xs">{w.product_name}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{fmt(w.issued_at)}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">
                    {w.is_lifetime ? "Lifetime" : fmt(w.expires_at)}
                    {w.is_capped_by_registration && <span className="ml-1 text-[#F59E0B]" title="Capped by vehicle registration">*</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
                      style={{ background: STATUS_CLR[w.status] + "22", color: STATUS_CLR[w.status], border: `1px solid ${STATUS_CLR[w.status]}55` }}>
                      {w.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button data-testid={`download-pdf-${w.id}`} onClick={() => downloadPdf(w.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E5E7EB] rounded hover:bg-[#E5E7EB]">
                      <Download size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
    </div>
  );
}
function fmt(d) { if (!d) return "-"; try { return new Date(d).toLocaleDateString("en-IN"); } catch { return d; } }
